import React, { useState, useEffect } from 'react';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFRiskAssessmentEngine = ({ smsfState, setSmsfState }) => {
    const [currentSection, setCurrentSection] = useState(0);
    const [answers, setAnswers] = useState({});
    const [riskScore, setRiskScore] = useState(null);
    const [showResults, setShowResults] = useState(false);
    
    // Simple inputs (Tests 836-840)
    const [age, setAge] = useState('');
    const [yearsToRetirement, setYearsToRetirement] = useState('');
    const [incomeStability, setIncomeStability] = useState('medium');
    const [dependents, setDependents] = useState('');
    
    // Scroll to questions when section changes
    useEffect(() => {
        const questionsSection = document.getElementById('risk-questions-section');
        if (questionsSection) {
            questionsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentSection]);

    // Comprehensive risk assessment sections
    const riskSections = [
        {
            id: 'investment_risk',
            title: 'Investment Risk Profile',
            description: 'Assess your comfort with investment volatility and risk',
            questions: [
                {
                    id: 'volatility_tolerance',
                    question: 'How would you react to a 20% decline in your SMSF portfolio value?',
                    options: [
                        { value: 1, label: 'Sell immediately to prevent further losses', riskLevel: 'Very Low' },
                        { value: 2, label: 'Monitor closely and consider reducing exposure', riskLevel: 'Low' },
                        { value: 3, label: 'Accept as normal market volatility', riskLevel: 'Medium' },
                        { value: 4, label: 'See it as an opportunity to buy more', riskLevel: 'High' },
                        { value: 5, label: 'Increase leverage to maximize gains', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'time_horizon',
                    question: 'What is your primary investment time horizon for your SMSF?',
                    options: [
                        { value: 1, label: '1-3 years (Short term)', riskLevel: 'Very Low' },
                        { value: 2, label: '3-7 years (Medium term)', riskLevel: 'Low' },
                        { value: 3, label: '7-15 years (Long term)', riskLevel: 'Medium' },
                        { value: 4, label: '15-25 years (Very long term)', riskLevel: 'High' },
                        { value: 5, label: '25+ years (Generational)', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'investment_experience',
                    question: 'What is your level of investment experience?',
                    options: [
                        { value: 1, label: 'Beginner - Limited investment experience', riskLevel: 'Very Low' },
                        { value: 2, label: 'Some experience with basic investments', riskLevel: 'Low' },
                        { value: 3, label: 'Moderate experience with various asset classes', riskLevel: 'Medium' },
                        { value: 4, label: 'Experienced investor with complex strategies', riskLevel: 'High' },
                        { value: 5, label: 'Expert investor with sophisticated knowledge', riskLevel: 'Very High' }
                    ]
                }
            ]
        },
        {
            id: 'liquidity_risk',
            title: 'Liquidity Risk Assessment',
            description: 'Evaluate your need for liquid assets and cash flow requirements',
            questions: [
                {
                    id: 'cash_flow_needs',
                    question: 'How much of your SMSF do you need to keep liquid for regular expenses?',
                    options: [
                        { value: 1, label: 'More than 50% for immediate needs', riskLevel: 'Very Low' },
                        { value: 2, label: '25-50% for regular expenses', riskLevel: 'Low' },
                        { value: 3, label: '10-25% for occasional needs', riskLevel: 'Medium' },
                        { value: 4, label: '5-10% for emergencies only', riskLevel: 'High' },
                        { value: 5, label: 'Less than 5% - all invested', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'pension_requirements',
                    question: 'When do you plan to start drawing an income from your SMSF?',
                    options: [
                        { value: 1, label: 'Already drawing pension income', riskLevel: 'Very Low' },
                        { value: 2, label: 'Within 5 years', riskLevel: 'Low' },
                        { value: 3, label: '5-10 years', riskLevel: 'Medium' },
                        { value: 4, label: '10-15 years', riskLevel: 'High' },
                        { value: 5, label: '15+ years', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'emergency_fund',
                    question: 'Do you have separate emergency funds outside your SMSF?',
                    options: [
                        { value: 1, label: 'Yes, substantial emergency fund (12+ months expenses)', riskLevel: 'Very High' },
                        { value: 2, label: 'Yes, adequate emergency fund (6-12 months)', riskLevel: 'High' },
                        { value: 3, label: 'Yes, basic emergency fund (3-6 months)', riskLevel: 'Medium' },
                        { value: 4, label: 'Limited emergency fund (1-3 months)', riskLevel: 'Low' },
                        { value: 5, label: 'No separate emergency fund', riskLevel: 'Very Low' }
                    ]
                }
            ]
        },
        {
            id: 'concentration_risk',
            title: 'Concentration Risk Analysis',
            description: 'Assess diversification and concentration in your portfolio',
            questions: [
                {
                    id: 'asset_diversification',
                    question: 'How diversified is your SMSF portfolio across asset classes?',
                    options: [
                        { value: 1, label: 'Very concentrated (1-2 asset classes)', riskLevel: 'Very High' },
                        { value: 2, label: 'Somewhat concentrated (2-3 asset classes)', riskLevel: 'High' },
                        { value: 3, label: 'Moderately diversified (3-4 asset classes)', riskLevel: 'Medium' },
                        { value: 4, label: 'Well diversified (4-5 asset classes)', riskLevel: 'Low' },
                        { value: 5, label: 'Highly diversified (5+ asset classes)', riskLevel: 'Very Low' }
                    ]
                },
                {
                    id: 'geographic_diversification',
                    question: 'How geographically diversified are your investments?',
                    options: [
                        { value: 1, label: 'Australia only', riskLevel: 'Very High' },
                        { value: 2, label: 'Australia and New Zealand', riskLevel: 'High' },
                        { value: 3, label: 'Australia and some international', riskLevel: 'Medium' },
                        { value: 4, label: 'Australia and significant international', riskLevel: 'Low' },
                        { value: 5, label: 'Global diversification', riskLevel: 'Very Low' }
                    ]
                },
                {
                    id: 'sector_concentration',
                    question: 'How concentrated are your investments in specific sectors?',
                    options: [
                        { value: 1, label: 'Very concentrated in 1-2 sectors', riskLevel: 'Very High' },
                        { value: 2, label: 'Concentrated in 2-3 sectors', riskLevel: 'High' },
                        { value: 3, label: 'Moderate concentration in 3-4 sectors', riskLevel: 'Medium' },
                        { value: 4, label: 'Well spread across 4-5 sectors', riskLevel: 'Low' },
                        { value: 5, label: 'Highly diversified across many sectors', riskLevel: 'Very Low' }
                    ]
                }
            ]
        },
        {
            id: 'operational_risk',
            title: 'Operational Risk Factors',
            description: 'Evaluate SMSF operational and administrative risks',
            questions: [
                {
                    id: 'trustee_experience',
                    question: 'How experienced are the SMSF trustees with superannuation law?',
                    options: [
                        { value: 1, label: 'Very experienced with extensive knowledge', riskLevel: 'Very Low' },
                        { value: 2, label: 'Moderately experienced with good knowledge', riskLevel: 'Low' },
                        { value: 3, label: 'Some experience with basic knowledge', riskLevel: 'Medium' },
                        { value: 4, label: 'Limited experience with minimal knowledge', riskLevel: 'High' },
                        { value: 5, label: 'No experience - learning as we go', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'professional_support',
                    question: 'What level of professional support do you have?',
                    options: [
                        { value: 1, label: 'Full professional team (accountant, auditor, advisor)', riskLevel: 'Very Low' },
                        { value: 2, label: 'Most professionals (accountant, auditor)', riskLevel: 'Low' },
                        { value: 3, label: 'Basic professionals (accountant only)', riskLevel: 'Medium' },
                        { value: 4, label: 'Limited professional support', riskLevel: 'High' },
                        { value: 5, label: 'No professional support', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'record_keeping',
                    question: 'How well organized are your SMSF records and documentation?',
                    options: [
                        { value: 1, label: 'Excellent - fully organized and up to date', riskLevel: 'Very Low' },
                        { value: 2, label: 'Good - well organized with minor gaps', riskLevel: 'Low' },
                        { value: 3, label: 'Adequate - basic organization', riskLevel: 'Medium' },
                        { value: 4, label: 'Poor - disorganized with gaps', riskLevel: 'High' },
                        { value: 5, label: 'Very poor - minimal records', riskLevel: 'Very High' }
                    ]
                }
            ]
        },
        {
            id: 'regulatory_risk',
            title: 'Regulatory and Compliance Risk',
            description: 'Assess compliance and regulatory risk factors',
            questions: [
                {
                    id: 'compliance_history',
                    question: 'What is your SMSF\'s compliance history with the ATO?',
                    options: [
                        { value: 1, label: 'Excellent - no issues ever', riskLevel: 'Very Low' },
                        { value: 2, label: 'Good - minor issues resolved quickly', riskLevel: 'Low' },
                        { value: 3, label: 'Adequate - some issues in the past', riskLevel: 'Medium' },
                        { value: 4, label: 'Poor - multiple compliance issues', riskLevel: 'High' },
                        { value: 5, label: 'Very poor - ongoing compliance problems', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'regulatory_changes',
                    question: 'How do you stay informed about regulatory changes?',
                    options: [
                        { value: 1, label: 'Very proactive - multiple sources, regular updates', riskLevel: 'Very Low' },
                        { value: 2, label: 'Proactive - regular updates from professionals', riskLevel: 'Low' },
                        { value: 3, label: 'Moderate - occasional updates', riskLevel: 'Medium' },
                        { value: 4, label: 'Reactive - updates only when issues arise', riskLevel: 'High' },
                        { value: 5, label: 'Very reactive - rarely updated', riskLevel: 'Very High' }
                    ]
                },
                {
                    id: 'audit_preparedness',
                    question: 'How prepared are you for an ATO audit?',
                    options: [
                        { value: 1, label: 'Fully prepared - all documentation ready', riskLevel: 'Very Low' },
                        { value: 2, label: 'Well prepared - most documentation available', riskLevel: 'Low' },
                        { value: 3, label: 'Moderately prepared - basic documentation', riskLevel: 'Medium' },
                        { value: 4, label: 'Poorly prepared - missing documentation', riskLevel: 'High' },
                        { value: 5, label: 'Very poorly prepared - minimal documentation', riskLevel: 'Very High' }
                    ]
                }
            ]
        }
    ];

    // Calculate overall risk score
    const calculateRiskScore = () => {
        if (Object.keys(answers).length === 0) return null;

        let totalScore = 0;
        let questionCount = 0;

        riskSections.forEach(section => {
            section.questions.forEach(question => {
                if (answers[question.id] !== undefined) {
                    totalScore += answers[question.id];
                    questionCount++;
                }
            });
        });

        if (questionCount === 0) return null;

        const averageScore = totalScore / questionCount;
        return {
            score: averageScore,
            level: averageScore <= 1.5 ? 'Very Low' :
                   averageScore <= 2.5 ? 'Low' :
                   averageScore <= 3.5 ? 'Medium' :
                   averageScore <= 4.5 ? 'High' : 'Very High',
            percentage: (averageScore / 5) * 100
        };
    };

    // Get risk recommendations
    const getRiskRecommendations = (riskLevel) => {
        const recommendations = {
            'Very Low': [
                'Consider increasing growth assets for better returns',
                'Review diversification opportunities',
                'Assess if you\'re being too conservative'
            ],
            'Low': [
                'Maintain current risk level',
                'Consider gradual increase in growth assets',
                'Ensure adequate diversification'
            ],
            'Medium': [
                'Current risk level appears appropriate',
                'Monitor portfolio performance regularly',
                'Maintain balanced approach'
            ],
            'High': [
                'Consider reducing risk exposure',
                'Increase defensive assets',
                'Review concentration risks'
            ],
            'Very High': [
                'Immediate risk reduction recommended',
                'Increase cash and defensive assets',
                'Consider professional advice',
                'Review all high-risk investments'
            ]
        };

        return recommendations[riskLevel] || [];
    };

    // Handle answer selection
    const handleAnswerSelect = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    // Check if current section is complete
    const isCurrentSectionComplete = () => {
        const currentSectionData = riskSections[currentSection];
        return currentSectionData.questions.every(question => answers[question.id] !== undefined);
    };

    // Check if all sections are complete
    const isAllSectionsComplete = () => {
        return riskSections.every(section => 
            section.questions.every(question => answers[question.id] !== undefined)
        );
    };

    // Calculate progress
    const calculateProgress = () => {
        let totalQuestions = 0;
        let answeredQuestions = 0;

        riskSections.forEach(section => {
            section.questions.forEach(question => {
                totalQuestions++;
                if (answers[question.id] !== undefined) {
                    answeredQuestions++;
                }
            });
        });

        return (answeredQuestions / totalQuestions) * 100;
    };

    const progress = calculateProgress();
    const currentSectionData = riskSections[currentSection];

    useEffect(() => {
        if (isAllSectionsComplete()) {
            const calculatedScore = calculateRiskScore();
            setRiskScore(calculatedScore);
        }
    }, [answers]);

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Risk Assessment Engine</h2>
                <div className="text-sm text-gray-600">
                    Progress: {Math.round(progress)}%
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white p-4 rounded-lg shadow-md">
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                    <span>Section {currentSection + 1} of {riskSections.length}</span>
                    <span>{Math.round(progress)}% Complete</span>
                </div>
            </div>

            {!showResults ? (
                <>
                    {/* Personal Information (Tests 836-840) */}
                    {currentSection === 0 && (
                        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                            <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={age}
                                        onChange={(e) => setAge(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., 55"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Factors into risk profile</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Years to Retirement</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={yearsToRetirement}
                                        onChange={(e) => setYearsToRetirement(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., 12"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Affects risk tolerance</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Income Stability</label>
                                    <select
                                        value={incomeStability}
                                        onChange={(e) => setIncomeStability(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="high">High - Stable government/corporate job</option>
                                        <option value="medium">Medium - Regular employment</option>
                                        <option value="low">Low - Variable income/self-employed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Dependents</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={dependents}
                                        onChange={(e) => setDependents(Number(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., 2"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Affects recommendations</p>
                                </div>
                            </div>
                        </div>
                    )}
                
                    {/* Current Section */}
                    <div id="risk-questions-section" className="bg-white p-6 rounded-lg shadow-md">
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                {currentSectionData.title}
                            </h3>
                            <p className="text-gray-600">{currentSectionData.description}</p>
                        </div>

                        <div className="space-y-6">
                            {currentSectionData.questions.map((question) => (
                                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-800 mb-4">{question.question}</h4>
                                    <div className="space-y-3">
                                        {question.options.map((option) => (
                                            <label key={option.value} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name={question.id}
                                                    value={option.value}
                                                    checked={answers[question.id] === option.value}
                                                    onChange={() => handleAnswerSelect(question.id, option.value)}
                                                    className="mr-3 text-blue-600"
                                                />
                                                <div className="flex-1">
                                                    <span className="text-gray-800">{option.label}</span>
                                                    <span className={`ml-2 px-2 py-1 text-xs rounded ${
                                                        option.riskLevel === 'Very Low' ? 'bg-green-100 text-green-800' :
                                                        option.riskLevel === 'Low' ? 'bg-blue-100 text-blue-800' :
                                                        option.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        option.riskLevel === 'High' ? 'bg-orange-100 text-orange-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {option.riskLevel}
                                                    </span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex justify-between items-center">
                            <button
                                onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
                                disabled={currentSection === 0}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ← Previous
                            </button>

                            <div className="flex space-x-2">
                                {riskSections.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentSection(index)}
                                        className={`w-3 h-3 rounded-full ${
                                            index === currentSection ? 'bg-blue-600' :
                                            riskSections[index].questions.every(q => answers[q.id] !== undefined) ? 'bg-green-500' :
                                            'bg-gray-300'
                                        }`}
                                        title={`Section ${index + 1}`}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={() => {
                                    if (currentSection < riskSections.length - 1) {
                                        setCurrentSection(currentSection + 1);
                                    } else if (isAllSectionsComplete()) {
                                        setShowResults(true);
                                    }
                                }}
                                disabled={!isCurrentSectionComplete()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {currentSection < riskSections.length - 1 ? 'Next →' : 'View Results'}
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                /* Results View */
                <div className="space-y-6">
                    {/* Risk Score Summary */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Risk Assessment Results</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* Test 841: Risk Score */}
                            <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="text-sm text-gray-600 mb-1">Risk Score</div>
                                <div className="text-4xl font-bold text-blue-600 mb-2">
                                    {Math.round((riskScore?.score || 2.5) * 20)}<span className="text-2xl">/100</span>
                                </div>
                                <div className="text-xs text-gray-500">Based on questionnaire</div>
                            </div>
                            {/* Test 842: Risk Profile */}
                            <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-sm text-gray-600 mb-1">Risk Profile</div>
                                <div className="text-3xl font-bold text-green-600 mb-2">
                                    {riskScore?.level || 'Moderate'}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {riskScore?.level === 'Very Low' || riskScore?.level === 'Low' ? 'Conservative' :
                                     riskScore?.level === 'Medium' ? 'Balanced' :
                                     riskScore?.level === 'High' || riskScore?.level === 'Very High' ? 'Growth' : 'Balanced'}
                                </div>
                            </div>
                        </div>
                        
                        {/* Test 843: Recommended Allocation */}
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg mb-4">
                            <h4 className="font-semibold text-purple-800 mb-3">Recommended Asset Allocation</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                {(() => {
                                    const level = riskScore?.level || 'Medium';
                                    const allocation = level === 'Very Low' || level === 'Low' 
                                        ? { cash: 30, bonds: 40, ausShares: 20, intlShares: 10 }
                                        : level === 'Medium'
                                        ? { cash: 10, bonds: 30, ausShares: 35, intlShares: 25 }
                                        : { cash: 5, bonds: 15, ausShares: 40, intlShares: 40 };
                                    
                                    return Object.entries(allocation).map(([asset, pct]) => (
                                        <div key={asset} className="flex justify-between p-2 bg-white rounded">
                                            <span className="capitalize">{asset.replace(/([A-Z])/g, ' $1')}</span>
                                            <span className="font-bold">{pct}%</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                    
                    {/* Test 844: Current vs Recommended */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Current vs Recommended Allocation</h3>
                        <div className="space-y-3">
                            {['Australian Shares', 'International Shares', 'Bonds', 'Cash'].map(asset => (
                                <div key={asset} className="border border-gray-200 rounded-lg p-3">
                                    <div className="flex justify-between mb-2 text-sm">
                                        <span className="font-medium">{asset}</span>
                                        <div className="space-x-4">
                                            <span className="text-gray-600">Current: <strong>25%</strong></span>
                                            <span className="text-blue-600">Recommended: <strong>35%</strong></span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                            <div className="bg-gray-600 h-2 rounded-full" style={{ width: '25%' }}></div>
                                        </div>
                                        <div className="flex-1 bg-blue-200 rounded-full h-2">
                                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '35%' }}></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Test 845: Rebalancing Suggestions */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Rebalancing Suggestions</h3>
                        <div className="space-y-3">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-start">
                                    <span className="text-2xl mr-3">📈</span>
                                    <div>
                                        <div className="font-semibold text-green-800">Increase International Shares</div>
                                        <p className="text-sm text-green-700 mt-1">
                                            Add $50,000 to international shares to reach recommended 35% allocation
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                <div className="flex items-start">
                                    <span className="text-2xl mr-3">📉</span>
                                    <div>
                                        <div className="font-semibold text-orange-800">Reduce Cash Holdings</div>
                                        <p className="text-sm text-orange-700 mt-1">
                                            Move $30,000 from cash to growth assets for better long-term returns
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-start">
                                    <span className="text-2xl mr-3">⚖️</span>
                                    <div>
                                        <div className="font-semibold text-blue-800">Rebalance Quarterly</div>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Set up automatic rebalancing to maintain target allocation
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Breakdown */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Risk Breakdown by Category</h3>
                        <div className="space-y-4">
                            {riskSections.map((section) => {
                                const sectionAnswers = section.questions
                                    .map(q => answers[q.id])
                                    .filter(answer => answer !== undefined);
                                
                                const sectionScore = sectionAnswers.length > 0 
                                    ? sectionAnswers.reduce((sum, answer) => sum + answer, 0) / sectionAnswers.length 
                                    : 0;

                                return (
                                    <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="font-semibold">{section.title}</h4>
                                            <span className={`px-2 py-1 text-xs rounded ${
                                                sectionScore <= 1.5 ? 'bg-green-100 text-green-800' :
                                                sectionScore <= 2.5 ? 'bg-blue-100 text-blue-800' :
                                                sectionScore <= 3.5 ? 'bg-yellow-100 text-yellow-800' :
                                                sectionScore <= 4.5 ? 'bg-orange-100 text-orange-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {sectionScore.toFixed(1)}/5.0
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full"
                                                style={{ width: `${(sectionScore / 5) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Risk Management Recommendations</h3>
                        <div className="space-y-3">
                            {getRiskRecommendations(riskScore?.level).map((recommendation, index) => (
                                <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                                    <span className="mr-3 text-blue-600">•</span>
                                    <span className="text-gray-700">{recommendation}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="flex justify-between">
                            <button
                                onClick={() => setShowResults(false)}
                                className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                                ← Back to Assessment
                            </button>
                            <button
                                onClick={() => {
                                    setAnswers({});
                                    setRiskScore(null);
                                    setCurrentSection(0);
                                    setShowResults(false);
                                }}
                                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Retake Assessment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMSFRiskAssessmentEngine;

